import { useEffect, useState } from 'react';
import { Panel } from '../../../layout/Panel';
import PieChartWithNeedle from '../../../layout/components/PieChartWithNeedle';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const dialData = [
    { name: 'High', value: 20, color: '#ef4444' },
    { name: 'Elevated', value: 20, color: '#f97316' },
    { name: 'Neutral', value: 20, color: '#facc15' },
    { name: 'Moderate', value: 20, color: '#84cc16' },
    { name: 'Low', value: 20, color: '#10b981' },
];

const dialValue = (score, min = -4, max = 4) => {
    const numericValue = Number(score ?? 0);
    return Math.max(0, Math.min(100, ((numericValue - min) / (max - min)) * 100));
};

const scoreText = (value) => {
    const numericValue = Number(value ?? 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (numericValue > 0) return "border-lime-200 bg-lime-50 text-lime-700";
    if (numericValue === 0) return "border-slate-200 bg-slate-50 text-slate-600";
    if (numericValue > -2) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-red-200 bg-red-50 text-red-700";
};

const riskLabel = (value) => {
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "Low concern";
    if (numericValue > 0) return "Moderate-low";
    if (numericValue === 0) return "Neutral";
    if (numericValue > -2) return "Elevated";
    return "High concern";
};

const RiskCard = ({ title, value, description }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Risk factor</div>
                <h3 className="mt-1 text-lg font-bold text-slate-950">{title}</h3>
            </div>
            <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(value)}`}>
                {scoreText(value)}
            </div>
        </div>
        <div className={`mt-4 w-fit rounded-md border px-2 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreTone(value)}`}>
            {riskLabel(value)}
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">{description}</p>
    </div>
);

const RiskRegisterRow = ({ label, status, detail }) => (
    <div className="grid gap-3 border-t border-slate-100 py-4 md:grid-cols-[180px_140px_minmax(0,1fr)] md:items-center">
        <div className="text-sm font-bold text-slate-950">{label}</div>
        <div className={`w-fit rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreTone(status)}`}>
            {riskLabel(status)}
        </div>
        <div className="text-sm leading-6 text-slate-600">{detail}</div>
    </div>
);

const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    if (Math.abs(numericValue) >= 1000) return `$${(numericValue / 1000).toFixed(1)}bn`;
    return `$${numericValue.toFixed(0)}m`;
};

const marketRiskIntensity = (value) => {
    const numericValue = Number(value || 0);
    if (numericValue >= 2) return 0.25;
    if (numericValue >= 1) return 0.5;
    if (numericValue >= 0) return 0.75;
    if (numericValue >= -1) return 1;
    if (numericValue >= -2) return 1.25;
    return 1.55;
};

const buildMarketScenarioData = (marketRisk, marketRiskData) => {
    if (marketRiskData?.scenarios?.length) {
        return marketRiskData.scenarios.map((scenario) => ({
            name: scenario.name,
            label: scenario.label,
            probability: Number(scenario.probability || 0),
            value: Number(scenario.value || 0),
            change: Number(scenario.change || 0),
            changePct: Number(scenario.changePct || 0),
            discountRate: Number(scenario.discountRate || 0),
            carbonPrice: Number(scenario.carbonPrice || 0),
            carbonCost: Number(scenario.carbonCost || 0),
            mainRiskDriver: scenario.mainRiskDriver,
            summary: scenario.summary,
        }));
    }

    const intensity = marketRiskIntensity(marketRisk);
    const baseValue = 1000;
    const scenarios = [
        {
            name: "Base",
            label: "Current model",
            probability: 35,
            value: baseValue,
            summary: "Business-as-usual valuation before climate or transition stress.",
        },
        {
            name: "Orderly",
            label: "Orderly transition",
            probability: 30,
            value: baseValue * (1 - 0.06 * intensity),
            summary: "Gradual policy action, manageable capex, and limited margin pressure.",
        },
        {
            name: "Disorderly",
            label: "Disorderly transition",
            probability: 22,
            value: baseValue * (1 - 0.16 * intensity),
            summary: "Late policy action, abrupt cost increases, and higher discount rates.",
        },
        {
            name: "Hot House",
            label: "Hot house world",
            probability: 13,
            value: baseValue * (1 - 0.25 * intensity),
            summary: "Higher physical disruption, weaker demand assumptions, and risk premium expansion.",
        },
    ];

    return scenarios.map((scenario) => ({
        ...scenario,
        value: Math.round(scenario.value),
        change: Math.round(scenario.value - baseValue),
        changePct: ((scenario.value - baseValue) / baseValue) * 100,
    }));
};

const buildMarketDrivers = (marketRisk, marketRiskData) => {
    if (marketRiskData?.drivers?.length) {
        return marketRiskData.drivers.map((driver) => ({
            name: driver.name,
            impact: Number(driver.impact || 0),
            note: driver.note,
        }));
    }

    const intensity = marketRiskIntensity(marketRisk);
    return [
        { name: "Carbon price", impact: Math.round(-8 * intensity), note: "Carbon costs, permits, taxes, and compliance exposure." },
        { name: "Energy cost", impact: Math.round(-6 * intensity), note: "Fuel, power, and input-cost volatility under transition scenarios." },
        { name: "Demand", impact: Math.round(-5 * intensity), note: "Revenue pressure from customer behaviour, substitution, and macro conditions." },
        { name: "Capex", impact: Math.round(-4 * intensity), note: "Transition investment, fleet or asset upgrades, and adaptation spend." },
        { name: "Discount rate", impact: Math.round(-7 * intensity), note: "Higher perceived risk increases WACC and lowers present value." },
    ];
};

const MarketSummaryMetric = ({ label, value, detail, tone = "slate" }) => {
    const tones = {
        slate: "border-slate-200 bg-slate-50 text-slate-950",
        red: "border-red-200 bg-red-50 text-red-700",
        emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
        amber: "border-amber-200 bg-amber-50 text-amber-700",
    };

    return (
        <div className={`rounded-lg border p-4 ${tones[tone] || tones.slate}`}>
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] opacity-70">{label}</div>
            <div className="mt-2 text-2xl font-black">{value}</div>
            <p className="mt-2 text-xs font-semibold leading-5 opacity-75">{detail}</p>
        </div>
    );
};

const scenarioTone = (changePct) => {
    if (changePct < -20) return "#ef4444";
    if (changePct < -10) return "#f59e0b";
    if (changePct < 0) return "#14b8a6";
    return "#10b981";
};

const ScenarioCard = ({ scenario, active, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect(scenario.name)}
        className={`rounded-lg border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md ${active ? "border-emerald-500 ring-2 ring-emerald-100" : "border-slate-200"}`}
    >
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{scenario.name}</div>
                <h4 className="mt-1 text-sm font-bold text-slate-950">{scenario.label}</h4>
            </div>
            <div className={`rounded-full px-2 py-1 text-xs font-bold ${scenario.changePct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                {scenario.changePct.toFixed(1)}%
            </div>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
            <div className="text-2xl font-black text-slate-950">{formatCurrency(scenario.value)}</div>
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{scenario.probability}% weight</div>
        </div>
        <p className="mt-3 text-xs font-medium leading-5 text-slate-500">{scenario.summary}</p>
    </button>
);

const MarketRiskSection = ({ marketRisk, marketRiskData }) => {
    const scenarios = buildMarketScenarioData(marketRisk, marketRiskData);
    const drivers = buildMarketDrivers(marketRisk, marketRiskData);
    const baseScenario = scenarios.find((scenario) => scenario.name === "Base") || scenarios[0];
    const defaultScenarioName = scenarios.find((scenario) => scenario.name === "Orderly")?.name || baseScenario?.name;
    const [selectedScenarioName, setSelectedScenarioName] = useState(defaultScenarioName);
    useEffect(() => {
        setSelectedScenarioName(defaultScenarioName);
    }, [defaultScenarioName]);
    const selectedScenario = scenarios.find((scenario) => scenario.name === selectedScenarioName) || scenarios.find((scenario) => scenario.name === "Orderly") || baseScenario;
    const baseValue = Number(marketRiskData?.result?.baseValuation || baseScenario?.value || 1000);
    const weightedValue = Number(marketRiskData?.result?.weightedScenarioValuation || scenarios.reduce((total, scenario) => total + scenario.value * (scenario.probability / 100), 0));
    const weightedDownsidePct = Number(marketRiskData?.result?.valuationAtRiskPct ?? (((weightedValue - baseValue) / baseValue) * 100));
    const worstScenario = scenarios.reduce((worst, scenario) => (scenario.value < worst.value ? scenario : worst), scenarios[0]);
    const worstDownsidePct = Number(marketRiskData?.result?.worstCaseDownsidePct ?? worstScenario.changePct);
    const selectedScenarioValue = Number(selectedScenario?.value || 0);
    const selectedScenarioChangePct = Number(selectedScenario?.changePct || 0);
    const selectedScenarioChange = Number(selectedScenario?.change || selectedScenarioValue - baseValue);
    const modelSource = marketRiskData?.assumption?.source ? marketRiskData.assumption.source.replaceAll("_", " ") : "illustrative fallback";
    const valuationDate = marketRiskData?.assumption?.valuationDate || "local preview";
    const marketTone = selectedScenarioChangePct <= -20 ? "red" : selectedScenarioChangePct <= -10 ? "amber" : "emerald";

    return (
        <Panel className="border border-slate-200 shadow-sm">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_260px] xl:items-start">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Market risk</p>
                    <h3 className="mt-1 text-xl font-black text-slate-950">Scenario-adjusted valuation risk</h3>
                    <p className="mt-2 max-w-4xl text-sm font-medium leading-6 text-slate-500">
                        This view translates climate and transition scenarios into possible valuation outcomes. It focuses on what could happen to stock-market value rather than the harm caused by the company.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                            {valuationDate}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                            {modelSource}
                        </span>
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-emerald-700">
                            Baseline: Orderly
                        </span>
                    </div>
                </div>
                <div className="flex justify-center xl:justify-end">
                    <PieChartWithNeedle data={dialData} width={190} height={145} value={dialValue(marketRisk)} />
                </div>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-3">
                <MarketSummaryMetric
                    label={`${selectedScenario?.name || "Scenario"} value`}
                    value={formatCurrency(selectedScenarioValue)}
                    detail={`${selectedScenario?.label || "Selected scenario"} valuation compared with the base model.`}
                    tone={marketTone}
                />
                <MarketSummaryMetric
                    label="Valuation at risk"
                    value={`${selectedScenarioChangePct.toFixed(1)}%`}
                    detail={`${formatCurrency(selectedScenarioChange)} difference versus the base valuation.`}
                    tone={marketTone}
                />
                <MarketSummaryMetric
                    label="Weighted model"
                    value={`${weightedDownsidePct.toFixed(1)}%`}
                    detail={`Worst case: ${marketRiskData?.result?.worstCaseScenario || worstScenario.label} at ${worstDownsidePct.toFixed(1)}%.`}
                    tone={weightedDownsidePct <= -20 ? "red" : weightedDownsidePct <= -10 ? "amber" : "emerald"}
                />
            </div>

            <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-start justify-between gap-4">
                        <div>
                            <h4 className="text-sm font-bold text-slate-950">Valuation by scenario</h4>
                            <p className="mt-1 text-xs font-semibold text-slate-500">
                                Select a bar or scenario card to update the valuation summary. Orderly is the baseline view.
                            </p>
                        </div>
                        <div className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreTone(marketRisk)}`}>
                            {riskLabel(marketRisk)}
                        </div>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-2">
                        {scenarios.map((scenario) => (
                            <button
                                key={scenario.name}
                                type="button"
                                onClick={() => setSelectedScenarioName(scenario.name)}
                                className={`rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition ${selectedScenarioName === scenario.name ? "border-emerald-500 bg-emerald-600 text-white shadow-sm" : "border-slate-200 bg-white text-slate-500 hover:border-emerald-300 hover:text-emerald-700"}`}
                            >
                                {scenario.name}
                            </button>
                        ))}
                    </div>
                    <div className="h-[300px] min-w-0">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={scenarios} margin={{ top: 16, right: 18, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 12 }} />
                                <YAxis tickFormatter={formatCurrency} stroke="#64748b" tick={{ fontSize: 12 }} />
                                <Tooltip
                                    formatter={(value, name) => [formatCurrency(value), name === "value" ? "Valuation" : name]}
                                    labelFormatter={(label) => `${label} scenario`}
                                />
                                <ReferenceLine y={baseValue} stroke="#94a3b8" strokeDasharray="4 4" />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]} onClick={(entry) => setSelectedScenarioName(entry.name)}>
                                    {scenarios.map((entry) => (
                                        <Cell
                                            key={entry.name}
                                            fill={scenarioTone(entry.changePct)}
                                            stroke={selectedScenarioName === entry.name ? "#064e3b" : "transparent"}
                                            strokeWidth={selectedScenarioName === entry.name ? 3 : 0}
                                            cursor="pointer"
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="grid gap-3">
                    {scenarios.map((scenario) => (
                        <ScenarioCard
                            key={scenario.name}
                            scenario={scenario}
                            active={selectedScenarioName === scenario.name}
                            onSelect={setSelectedScenarioName}
                        />
                    ))}
                </div>
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3">
                    <h4 className="text-sm font-bold text-slate-950">Main valuation drivers</h4>
                    <p className="mt-1 text-xs font-semibold text-slate-500">Indicative contribution to valuation pressure. These become model outputs once scenario inputs are stored.</p>
                </div>
                <div className="h-[260px] min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={drivers} layout="vertical" margin={{ top: 6, right: 16, left: 44, bottom: 6 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                            <XAxis type="number" tickFormatter={(value) => `${value}%`} stroke="#64748b" tick={{ fontSize: 12 }} />
                            <YAxis type="category" dataKey="name" width={100} stroke="#64748b" tick={{ fontSize: 12 }} />
                            <Tooltip formatter={(value, name, props) => [`${value}%`, props.payload.note]} />
                            <ReferenceLine x={0} stroke="#94a3b8" />
                            <Bar dataKey="impact" fill="#0f766e" radius={[0, 6, 6, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Panel>
    );
};

const CorporateRiskSection = ({ corporateRisk, riskData }) => (
    <details className="group rounded-lg border border-slate-200 bg-white shadow-sm" open>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5">
            <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Corporate risk</p>
                <h3 className="mt-1 text-xl font-black text-slate-950">Company impact on environment and society</h3>
                <p className="mt-2 max-w-4xl text-sm font-medium leading-6 text-slate-500">
                    Outward materiality: how the company's emissions, operations, and conduct may create environmental, social, and stakeholder risk.
                </p>
            </div>
            <div className="shrink-0 rounded-full border border-slate-200 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500 group-open:hidden">
                Show
            </div>
            <div className="hidden shrink-0 rounded-full border border-slate-200 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500 group-open:block">
                Hide
            </div>
        </summary>
        <div className="grid gap-4 border-t border-slate-100 p-5">
            <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)] xl:items-center">
                <div className="flex justify-center">
                    <PieChartWithNeedle data={dialData} width={200} height={160} value={dialValue(corporateRisk)} />
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                    <MarketSummaryMetric
                        label="Corporate score"
                        value={scoreText(corporateRisk)}
                        detail="Existing platform score for external company risk."
                        tone={corporateRisk < -1 ? "red" : corporateRisk < 0 ? "amber" : "emerald"}
                    />
                    <MarketSummaryMetric
                        label="Temperature data"
                        value={riskData?.temp?.length ? "Available" : "Missing"}
                        detail="Climate impact pathway records connected to this company."
                        tone={riskData?.temp?.length ? "emerald" : "amber"}
                    />
                    <MarketSummaryMetric
                        label="Cost data"
                        value={riskData?.cost?.length ? "Available" : "Missing"}
                        detail="Climate cost pathway records connected to this company."
                        tone={riskData?.cost?.length ? "emerald" : "amber"}
                    />
                </div>
            </div>
            <RiskGraph
                title="Temperature pathway"
                description="Projected climate impact expressed as global mean temperature contribution under available emissions scopes."
                data={riskData?.temp}
                transformFactor={1000}
                unit="deg C"
            />
            <RiskGraph
                title="Climate cost pathway"
                description="Estimated economic harm associated with available emissions scopes over the modeled period."
                data={riskData?.cost}
                transformFactor={1}
                unit="USD bn"
            />
        </div>
    </details>
);

const transformRiskData = (data = []) => data
    .filter((item) => item?.name)
    .map((item) => ({
        ...item,
        name: new Date(item.name).getTime(),
    }));

const RiskGraph = ({ title, description, data, transformFactor, unit }) => {
    const transformedData = transformRiskData(data);

    if (!transformedData.length) {
        return (
            <Panel className="border border-slate-200 shadow-sm">
                <h3 className="text-lg font-bold text-slate-950">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
                <div className="mt-5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm font-semibold text-slate-500">
                    Climate time-series data is not available for this company yet.
                </div>
            </Panel>
        );
    }

    return (
        <Panel className="border border-slate-200 shadow-sm">
            <div className="mb-4">
                <h3 className="text-lg font-bold text-slate-950">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
            </div>
            <div className="h-[320px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={transformedData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="name"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(value) => new Date(value).getFullYear()}
                            stroke="#64748b"
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            tickFormatter={(value) => `${(value / transformFactor).toFixed(1)}`}
                            stroke="#64748b"
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                            formatter={(value, name) => [`${(Number(value) / transformFactor).toFixed(2)} ${unit}`, name]}
                            labelFormatter={(label) => new Date(label).getFullYear()}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="scope1" stroke="#10b981" dot={false} strokeWidth={2} />
                        <Line type="monotone" dataKey="scope2" stroke="#2563eb" dot={false} strokeWidth={2} />
                        <Line type="monotone" dataKey="scope3" stroke="#f97316" dot={false} strokeWidth={2} />
                        <Line type="monotone" dataKey="scope12" stroke="#14b8a6" dot={false} strokeWidth={2} />
                        <Line type="monotone" dataKey="scope123" stroke="#ef4444" dot={false} strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Panel>
    );
};

export const RiskTab = ({ risk1, risk2, riskData, marketRiskData }) => {
    const corporateRisk = Number(risk1 || 0);
    const marketRisk = Number(risk2 || 0);
    const totalRisk = corporateRisk + marketRisk;

    return (
        <div className="space-y-4">
            <Panel className="border border-slate-200 shadow-sm">
                <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-center">
                    <div className="flex justify-center">
                        <PieChartWithNeedle data={dialData} width={200} height={160} value={dialValue(totalRisk)} />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Risk view</p>
                        <h2 className="mt-2 text-2xl font-bold text-slate-950">Risk Score {scoreText(totalRisk)}</h2>
                        <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                            Risk is presented through dual materiality: how this company can affect the environment and society, and how market, regulatory, and climate conditions can affect the company's operations and value.
                        </p>
                    </div>
                    <div className={`w-fit rounded-full border px-4 py-2 text-sm font-bold ${scoreTone(totalRisk)}`}>
                        {riskLabel(totalRisk)}
                    </div>
                </div>
            </Panel>

            <div className="grid gap-4 xl:grid-cols-2">
                <RiskCard
                    title="Corporate Risk"
                    value={corporateRisk}
                    description="Outward materiality: how this company's profile, operations, and conduct may create risk for the environment, society, stakeholders, and long-term trust."
                />
                <RiskCard
                    title="Market Risk"
                    value={marketRisk}
                    description="Inward materiality: how regulation, adverse climate scenarios, sector pressure, market volatility, and transition costs may affect company operations and valuation."
                />
            </div>

            <MarketRiskSection marketRisk={marketRisk} marketRiskData={marketRiskData} />

            <Panel className="border border-slate-200 shadow-sm">
                <div className="mb-2">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Risk register</p>
                    <h3 className="mt-1 text-lg font-bold text-slate-950">Current research flags</h3>
                </div>
                <RiskRegisterRow
                    label="Corporate"
                    status={corporateRisk}
                    detail="Assess the company's potential external impact on environment, society, stakeholder confidence, and licence to operate."
                />
                <RiskRegisterRow
                    label="Market"
                    status={marketRisk}
                    detail="Monitor how regulation, climate scenarios, capital costs, sector rotation, and market pressure may flow back into business value."
                />
                <RiskRegisterRow
                    label="Climate data"
                    status={riskData?.temp?.length ? 1 : -1}
                    detail={riskData?.temp?.length ? "Climate time-series data is available for review." : "Climate time-series data is not yet available for this company."}
                />
            </Panel>

            <CorporateRiskSection corporateRisk={corporateRisk} riskData={riskData} />
        </div>
    );
};
